/**
 * PlayerPageHeaderInnerContainer - Inner component for player page header.
 * Uses chart capture context for PDF generation. Must be rendered inside ChartCaptureProvider.
 * The active tab is remembered per player in playerTabMemoryVar, so switching
 * lists preserves it and opening a different player does not inherit theirs.
 * @remarks Container component - handles all business logic
 */

import { useApolloClient, useReactiveVar } from "@apollo/client";
import { type Href, useRouter } from "expo-router";
import React, {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform } from "react-native";

import type { TabPressHandlers } from "@/features/player-detail/components/molecules/PlayerPageTabs/PlayerPageTabsView";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import ManuallyAddPlayerModal from "@/components/organisms/ManuallyAddPlayerModal";
import { useIsAdmin } from "@/features/auth";
import {
  BREAKDOWN_METRIC_SLUGS,
  type BreakdownMetric,
} from "@/features/player-metric-breakdown";
import {
  PlayerDetailPlayerMetaFragment,
  PlayerSearchPlayerFragment,
  usePlayerDetailDeleteManualPlayerMutation,
} from "@/generated/graphql";
import { useRecentPlayers } from "@/hooks/useRecentPlayers";
import { useToastNotification } from "@/hooks/useToastNotification";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import useExpandedPlayerUrl, {
  useGetExpandedPlayerUrl,
} from "@/hooks/useExpandedPlayerUrl";
import useMobileScreen from "@/hooks/useMobileScreen";
import { POSTHOG_FLAGS } from "@/config/featureFlags";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import {
  coercePlayerActiveTab,
  isExportingReportVar,
  playerTabMemoryVar,
  resolvePlayerActiveTab,
  selectPlayerTab,
  suppressAutoSelectVar,
} from "@/stores/player";
import { ManualPlayerEditData } from "@/types/manuallyAddPlayerModalTypes";

import DeletePlayerDialog from "@/features/player-detail/components/molecules/DeletePlayerDialog";
import ScopedWatchlistPicker from "@/features/player-detail/components/organisms/ScopedWatchlistPicker";
import SelectListSheet from "@/features/scouting/components/organisms/SelectListSheet";

import { useChartCapture } from "@/features/player-detail/providers/ChartCaptureProvider";
import {
  generatePlayerReportPdf,
  type PlayerReportPdfData,
} from "@/features/player-detail/utils/generatePlayerReportPdf";

import {
  getValuationBadgeAction,
  GPR_POTENTIAL_ENABLED,
  INDICATOR_CONFIGS,
} from "@/features/player-detail/utils/playerPageHeaderConstants";
import type { IndicatorMetricType } from "@/features/player-detail/utils/playerPageHeaderTypes";
import {
  buildPotentialIndicators,
  calculateMonthsRemaining,
  extractIndicatorData,
  extractPlayerIdentityData,
  formatScoreValue,
  formatValuationDisplay,
  getIndicatorValueColor,
  getTransferValueColor,
} from "@/features/player-detail/utils/playerPageHeaderUtils";
import PlayerPageHeaderSkeleton from "@/features/player-detail/components/molecules/PlayerPageHeaderSkeleton";
import PlayerPageHeaderView from "@/features/player-detail/components/organisms/PlayerPageHeader/PlayerPageHeaderView";
import PlayerPreviewBreakdownModal from "@/features/player-detail/components/organisms/PlayerPreviewBreakdownModal";
import TransferReportsModal from "@/features/player-detail/components/organisms/TransferReportsModal";

/**
 * Maps the indicator metric type back to the routed breakdown segment.
 * Returns null for indicators without a routed breakdown (monthsLeft).
 * @param type - The indicator metric type
 */
function mapIndicatorTypeToBreakdownSlug(
  type: IndicatorMetricType
): string | null {
  const breakdown: Partial<Record<IndicatorMetricType, BreakdownMetric>> = {
    gpr: "gpr",
    // SE-8157. No hasTransferValue-style guard is paired with this: the tile
    // is only pushed onto the rail when both bounds exist, so the arrow can
    // never advertise a breakdown with nothing behind it.
    potential: "potential",
    playerStyleFit: "playerStyleFit",
    teamStyleFit: "teamStyleFit",
    transferValue: "transferValue",
  };
  const key = breakdown[type];
  return key ? BREAKDOWN_METRIC_SLUGS[key] : null;
}

/**
 * Resolves the routed/in-place breakdown slug an indicator tile should open, or
 * null when the tile is not navigable. monthsLeft has no breakdown, and
 * transferValue with no valuation data has nothing meaningful to show. Extracted
 * to a module-level helper so the guard branches don't inflate the container's
 * cognitive complexity (SE-5473).
 * @param type - The pressed indicator metric type
 * @param indicatorData - Extracted indicator values for the current player
 * @param playerId - The current player's id, required to build the breakdown
 * @returns The breakdown slug to open, or null when the tile is a no-op
 */
function resolveIndicatorBreakdownSlug(
  type: IndicatorMetricType,
  indicatorData: ReturnType<typeof extractIndicatorData>,
  playerId: string | undefined
): string | null {
  if (!playerId) return null;
  if (type === "monthsLeft") return null;
  if (
    type === "transferValue" &&
    indicatorData.transferValueLow == null &&
    indicatorData.transferValueHigh == null &&
    indicatorData.publicValuation == null
  ) {
    return null;
  }
  return mapIndicatorTypeToBreakdownSlug(type);
}

/**
 * Holds the in-place KPI breakdown slug for the preview-modal context (SE-5473).
 * Resets to null whenever the player changes so a reused container instance
 * starts each player with no breakdown open — adjusting state during render on
 * an id change is the React-recommended alternative to a useEffect here.
 * @param playerId - The current player's id
 * @returns The open slug plus open/close handlers
 */
function useBreakdownSlug(playerId: string | undefined): {
  openBreakdownSlug: string | null;
  openBreakdown: (slug: string) => void;
  closeBreakdown: () => void;
} {
  const [openBreakdownSlug, setOpenBreakdownSlug] = useState<string | null>(
    null
  );
  const [breakdownPlayerId, setBreakdownPlayerId] = useState(playerId);
  if (playerId !== breakdownPlayerId) {
    setBreakdownPlayerId(playerId);
    setOpenBreakdownSlug(null);
  }
  const openBreakdown = useCallback((slug: string) => {
    setOpenBreakdownSlug(slug);
  }, []);
  const closeBreakdown = useCallback(() => {
    setOpenBreakdownSlug(null);
  }, []);
  return { openBreakdownSlug, openBreakdown, closeBreakdown };
}

/**
 * Format team display name including league when available
 * @param team - Player's team object (may be null)
 * @param teamName - Fallback team name string
 * @returns Formatted team display string
 */
function formatCurrentTeam(
  team: PlayerDetailPlayerMetaFragment["team"],
  teamName: string | null | undefined
): string {
  if (!team) return teamName ?? "";
  if (team.league) return `${team.name} - ${team.league.name}`;
  return team.name ?? "";
}

/**
 * Props for PlayerPageHeaderInnerContainer
 */
export interface PlayerPageHeaderInnerContainerProps {
  readonly player?: PlayerDetailPlayerMetaFragment | null;
  readonly loading: boolean;
  readonly isDark: boolean;
  /** Whether rendered inside a modal — threads through to view for compact layout */
  readonly isModal?: boolean;
}

/**
 * PlayerPageHeaderInnerContainer - Inner component that uses chart capture context
 * @param props - Container props
 * @param props.player - Player data fragment
 * @param props.loading - Whether player data is loading
 * @param props.isDark - Whether dark mode is active
 * @param props.isModal - Whether rendered inside a modal
 * @returns Header content with PDF generation capability
 */
const PlayerPageHeaderInnerContainer = ({
  player,
  loading,
  isDark,
  isModal = false,
}: PlayerPageHeaderInnerContainerProps) => {
  const isSmallScreen = useMobileScreen();
  // SE-5062: re-enable the download entry point, gated to desktop web. The PDF
  // capture path (html2canvas) is web-only, and SE-4833 disabled the button
  // after it broke on mobile. Rollout is now controlled by a PostHog flag
  // (hidden by default, opt-in) instead of the backend feature-flag + admin
  // auto-grant.
  const isDownloadReportFlagEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.PLAYER_DOWNLOAD_REPORT) === true;
  const isDownloadReportEnabled =
    isDownloadReportFlagEnabled && Platform.OS === "web" && !isSmallScreen;
  // Tab state — remembered per player, so switching lists preserves the tab
  // and opening a different player does not inherit theirs (SE-8285). The
  // displayed tab is DERIVED below, once the player's identity and the
  // Financials flag are known.
  const tabMemory = useReactiveVar(playerTabMemoryVar);
  const playerId = player?.id ?? null;

  // Tab press handlers — one per tab, each naming the player it is about
  const tabPressHandlers: TabPressHandlers = useMemo(
    () => ({
      overview: () => selectPlayerTab(playerId, "overview"),
      dataHub: () => selectPlayerTab(playerId, "dataHub"),
      reportsNotes: () => selectPlayerTab(playerId, "reportsNotes"),
      similarPlayers: () => selectPlayerTab(playerId, "similarPlayers"),
      financials: () => selectPlayerTab(playerId, "financials"),
    }),
    [playerId]
  );

  const isAdmin = useIsAdmin();

  // SE-4643: Financials tab gated by PostHog feature flag only (same
  // rule as squad planning). Role-based OWNER gate removed — targeting
  // is now entirely managed in PostHog.
  // SE-8484: bare key — per-environment targeting now lives in PostHog itself
  // via the `environment` person-property release condition, not a suffixed
  // key, so this and the other Squad Compliance surfaces read the same key.
  const isOwner =
    usePostHogFeatureFlag(POSTHOG_FLAGS.SQUAD_FINANCIALS_TAB) === true;

  // Extract player identity data
  const identityData = useMemo(
    () => extractPlayerIdentityData(player, Platform.OS),
    [player]
  );

  // The tab on screen. Narrowed to one this player actually offers: manual
  // players render only Reports & Notes (plus Financials when the flag is on),
  // and nobody without the flag may sit on Financials. Derived rather than
  // corrected by a write — a correcting write fires while the PREVIOUS player
  // is still the rendered one and overwrites the selection a quick action just
  // made for the player being navigated to (SE-8285).
  const activeTab = coercePlayerActiveTab(
    resolvePlayerActiveTab(tabMemory, playerId),
    identityData.isManual,
    isOwner
  );

  // Extract indicator data
  const indicatorData = useMemo(() => extractIndicatorData(player), [player]);

  // Chart capture for PDF generation - NOW INSIDE the Provider
  const { captureCharts, awaitChartsReady } = useChartCapture();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  // Re-entrancy guard for the export — a ref so a rapid second click is ignored
  // regardless of React state-update timing (SE-5062).
  const exportInFlightRef = useRef(false);

  // Download report handler - generates PDF with player data and charts
  const handleDownloadReport = useCallback(async () => {
    // SE-5469: gate on the backend-canonical name so mononym players
    // (e.g. "Neymar") aren't blocked by missing firstName/lastName parts.
    if (!player?.id || !player?.name) {
      return;
    }

    if (Platform.OS !== "web") {
      return;
    }

    // Ignore double-clicks while an export is already running.
    if (exportInFlightRef.current) {
      return;
    }
    exportInFlightRef.current = true;

    // SE-5062: force DataHubTab to mount on demand so its chart refs register,
    // then wait for the charts to finish rendering before capturing. The pre-
    // mount that used to keep refs warm was removed in SE-5038.
    isExportingReportVar(true);
    setIsGeneratingPdf(true);

    try {
      await awaitChartsReady();
      const charts = await captureCharts();

      const monthsRemaining = calculateMonthsRemaining(
        indicatorData.contractExpires
      );

      const pdfData: PlayerReportPdfData = {
        identity: {
          playerName: identityData.playerName,
          positions: identityData.positions,
          clubName: identityData.clubName,
          age: identityData.age,
          nationality: identityData.nationality,
          agency: identityData.agency,
        },
        metrics: {
          gpr: formatScoreValue(indicatorData.gpr),
          playerStyleFit: formatScoreValue(indicatorData.playerStyleFit),
          teamStyleFit: formatScoreValue(indicatorData.teamStyleFit),
          transferValue: formatValuationDisplay(
            indicatorData.transferValueLow,
            indicatorData.transferValueHigh,
            indicatorData.publicValuation
          ),
          contractMonths: monthsRemaining?.toString() ?? "—",
        },
        aiPlayerSummary: null,
        aiScoutingSummary: null,
        scoutingReports: [],
        notes: [],
        individualMetrics: [],
        charts: {
          spiderChart: charts.spiderChart,
          dotPlot: charts.dotPlot,
        },
      };

      await generatePlayerReportPdf(pdfData);
    } catch (error) {
      // eslint-disable-next-line no-console -- PDF generation error visible to developers
      console.error("Failed to generate PDF report:", error);
    } finally {
      setIsGeneratingPdf(false);
      // Reset the export signal so the on-demand DataHubTab unmounts again.
      isExportingReportVar(false);
      exportInFlightRef.current = false;
    }
  }, [player, captureCharts, awaitChartsReady, identityData, indicatorData]);

  // Add-to-list actionsheet (the list-membership editor sheet). Declared above
  // identityBlockProps so the SE-5084 mobile watchlist pills can reuse the same
  // opener the SE-5083 FAB uses — tapping a pill edits which lists the player
  // belongs to.
  const [showActionsheet, setShowActionsheet] = useState(false);
  const watchlistPromptRef = useRef<CustomPromptRefProps>(
    null
  ) as RefObject<CustomPromptRefProps>;

  const handleOpenActionsheet = useCallback(() => {
    setShowActionsheet(true);
  }, []);

  const handleCloseActionsheet = useCallback(() => {
    setShowActionsheet(false);
  }, []);

  // SE-5439/SE-5438: the mobile "add to list" sheet manages watchlist
  // membership through its own mutations, which don't touch the
  // player's `watchlists` field. Refetch the player so the membership
  // chips beneath the bio reflect the change once the sheet saves.
  const apolloClient = useApolloClient();
  const handleWatchlistMembershipChange = useCallback(() => {
    apolloClient
      .refetchQueries({ include: ["PlayerDetailGetPlayer"] })
      .catch(() => {
        // A failed refresh is non-fatal — the chips just stay stale
        // until the next natural refetch; swallow so it never surfaces.
      });
  }, [apolloClient]);

  // SE-5084: tapping a mobile watchlist pill opens the membership editor sheet.
  // The pill payload (watchlistId) is not needed by the sheet today — it lists
  // every watchlist with the current membership — so we discard it and reuse
  // the shared opener.
  const handleWatchlistPillPress = useCallback(
    (_watchlistId: string) => {
      setShowActionsheet(true);
    },
    [setShowActionsheet]
  );

  // Build identity block props
  const identityBlockProps = useMemo(
    () => ({
      playerName: identityData.playerName,
      roleArchetype: identityData.roleArchetype,
      secondaryRoleArchetype: identityData.secondaryRoleArchetype,
      valuationStatus: identityData.valuationStatus,
      valuationBadgeAction: getValuationBadgeAction(
        identityData.valuationStatus
      ),
      onLoanFrom: identityData.onLoanFrom,
      positions: identityData.positions,
      clubName: identityData.clubName,
      age: identityData.age,
      birthYear: identityData.birthYear,
      nationality: identityData.nationality,
      nationalityFlagUrl: identityData.nationalityFlagUrl,
      agency: identityData.agency,
      isInjured: identityData.isInjured,
      injuredUntil: identityData.injuredUntil,
      preferredFoot: identityData.preferredFoot,
      isManual: identityData.isManual,
      limitedDataWarning: identityData.limitedDataWarning,
      isDark,
      // SE-5024: "Saved in watchlists:" indicator beneath the identity rows.
      // Empty array hides the row.
      watchlists: player?.watchlists ?? [],
      // SE-5084: mobile-only interactive pills reuse the membership editor sheet.
      onWatchlistPress: handleWatchlistPillPress,
    }),
    [identityData, isDark, player, handleWatchlistPillPress]
  );

  const router = useRouter();
  const getExpandedUrl = useGetExpandedPlayerUrl();

  // SE-5473: inside the player preview modal, the KPI breakdown opens in place
  // (layered on top of the modal) instead of navigating. `openBreakdownSlug`
  // holds the metric slug of the breakdown to render, or null when none is open.
  const {
    openBreakdownSlug,
    openBreakdown,
    closeBreakdown: handleCloseBreakdown,
  } = useBreakdownSlug(player?.id);

  // SE-5024: indicator press handler navigates to the routed breakdown page
  // for the 4 primary KPIs. monthsLeft and transferValue-with-no-data still
  // no-op (the breakdown route would have nothing meaningful to render).
  // SE-5473: in the modal context a `router.push` here trips the global-search
  // teardown effect and tears the whole preview/search overlay down, stranding
  // the user on the breakdown page with no way back. When `isModal`, open the
  // breakdown in place instead so it layers on top and closing returns here.
  const handleIndicatorPress = useCallback(
    (type: IndicatorMetricType) => {
      const slug = resolveIndicatorBreakdownSlug(
        type,
        indicatorData,
        player?.id
      );
      if (!slug) return;
      if (isModal) {
        openBreakdown(slug);
        return;
      }
      const url = getExpandedUrl(`/players/${player?.id}/breakdown/${slug}`);
      router.push(url as Href);
    },
    [indicatorData, player, router, getExpandedUrl, isModal, openBreakdown]
  );

  // Build indicator rail props — manual players have no metric data
  const indicatorRailProps = useMemo(() => {
    if (identityData.isManual) {
      return { indicators: [] };
    }

    const monthsRemaining = calculateMonthsRemaining(
      indicatorData.contractExpires
    );
    const monthsDisplay = monthsRemaining == null ? "—" : `${monthsRemaining}`;

    // Mirror the early-return guard in handleIndicatorPress so the chevron
    // affordance only shows when the tile is actually navigable. Without this
    // the transferValue tile would advertise a routed breakdown that no-ops
    // for players with no valuation data.
    const hasTransferValue =
      indicatorData.transferValueLow != null ||
      indicatorData.transferValueHigh != null ||
      indicatorData.publicValuation != null;

    // SE-8154: sits second, right of GPR, and ONLY when both bounds exist. The
    // PRD gives a player outside the model's coverage (~89%) no card at all —
    // hence an omission from the array, never a placeholder tile. Breakdown
    // slug lands in SE-8157. Held off the rail by the SE-8777 kill switch
    // until SE-8724 ships the breakdown content behind it.
    const potentialTiles = GPR_POTENTIAL_ENABLED
      ? buildPotentialIndicators(
          indicatorData.floorGpr,
          indicatorData.ceilingGpr,
          isDark,
          () => handleIndicatorPress("potential")
        )
      : [];

    return {
      indicators: [
        {
          type: "gpr" as const,
          label: INDICATOR_CONFIGS.gpr.shortLabel,
          value: formatScoreValue(indicatorData.gpr),
          valueColor: getIndicatorValueColor("gpr", indicatorData.gpr, isDark),
          onPress: () => handleIndicatorPress("gpr"),
          showArrow: true,
        },
        ...potentialTiles,
        {
          type: "playerStyleFit" as const,
          label: INDICATOR_CONFIGS.playerStyleFit.shortLabel,
          value: formatScoreValue(indicatorData.playerStyleFit),
          valueColor: getIndicatorValueColor(
            "playerStyleFit",
            indicatorData.playerStyleFit,
            isDark
          ),
          onPress: () => handleIndicatorPress("playerStyleFit"),
          showArrow: true,
        },
        {
          type: "teamStyleFit" as const,
          label: INDICATOR_CONFIGS.teamStyleFit.shortLabel,
          value: formatScoreValue(indicatorData.teamStyleFit),
          valueColor: getIndicatorValueColor(
            "teamStyleFit",
            indicatorData.teamStyleFit,
            isDark
          ),
          onPress: () => handleIndicatorPress("teamStyleFit"),
          showArrow: true,
        },
        {
          type: "transferValue" as const,
          label: INDICATOR_CONFIGS.transferValue.shortLabel,
          value: formatValuationDisplay(
            indicatorData.transferValueLow,
            indicatorData.transferValueHigh,
            indicatorData.publicValuation
          ),
          valueColor: getTransferValueColor(
            indicatorData.geminiMax,
            indicatorData.geminiMin,
            isDark
          ),
          onPress: () => handleIndicatorPress("transferValue"),
          showArrow: hasTransferValue,
        },
        {
          type: "monthsLeft" as const,
          label: INDICATOR_CONFIGS.monthsLeft.shortLabel,
          value: monthsDisplay,
          valueColor: getIndicatorValueColor(
            "monthsLeft",
            monthsRemaining,
            isDark
          ),
          // No onPress: Months Left has no routed breakdown, so it renders as a
          // non-interactive card (no button cursor/hover) instead of a dead
          // Pressable that no-ops (SE-5446).
          showArrow: false,
        },
      ],
    };
  }, [identityData.isManual, indicatorData, handleIndicatorPress, isDark]);

  // Report creation handler — navigates to the matches page for this player
  const matchesUrl = useExpandedPlayerUrl(
    `/players/${player?.id ?? ""}/matches`
  );
  const handleAddReport = useCallback(() => {
    if (player?.id) {
      router.push(matchesUrl as Href);
    }
  }, [router, matchesUrl, player?.id]);

  const { handleAddWatchlistPress, loading: watchlistLoading } =
    useCreateWatchlistActions({ promptRefForModal: watchlistPromptRef });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const editData: ManualPlayerEditData | undefined = useMemo(() => {
    if (!player || !identityData.isManual) return undefined;

    return {
      playerId: player.id,
      firstName: player.firstName ?? "",
      lastName: player.lastName ?? "",
      positionIds: (player.positions ?? [])
        .map(p => p?.id)
        .filter((p): p is string => !!p),
      currentTeam: formatCurrentTeam(player.team, player.teamName),
      currentTeamId: player.team?.id ?? "",
      nationalityId: player.nationality?.id ?? "",
      dateOfBirth: player.dateOfBirth?.split("T")[0] ?? "",
    };
  }, [player, identityData.isManual]);

  const handleEditPlayer = useCallback(() => {
    setIsEditModalOpen(true);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
  }, []);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const toast = useToastNotification();
  const { removeRecentPlayer } = useRecentPlayers();

  const [deleteManualPlayer, { loading: isDeletingPlayer }] =
    usePlayerDetailDeleteManualPlayerMutation({
      refetchQueries: [
        "RosterManagementListMyOrganizationsPlayers",
        "KanbanListMyOrganizationsPlayers",
        "PlayerSearchFilterPlayers",
        "PlayerSearchSearchPlayers",
      ],
      awaitRefetchQueries: true,
    });

  // Permission: show delete for all manual players — backend enforces
  // creator/admin restriction and returns an error if unauthorized.

  const handleDeletePlayer = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteDialogClose = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!player?.id) return;
    try {
      await deleteManualPlayer({
        variables: { input: { id: player.id } },
        // Evict from Apollo cache immediately so every cached list drops
        // the deleted player without having to enumerate refetchQueries.
        update(cache) {
          cache.evict({
            id: cache.identify({ __typename: "Player", id: player.id }),
          });
          cache.gc();
        },
      });
      // Remove from the local recent-players store so the global "+" menu
      // no longer shows a zombie entry pointing at a deleted profile.
      removeRecentPlayer(player.id);
      toast.show({
        action: "success",
        description: `Player "${player.name}" deleted successfully`,
      });
      // Suppress desktop auto-select so user sees the empty state, then
      // navigate — router.replace prevents the deleted page staying in stack.
      suppressAutoSelectVar(true);
      router.replace("/players" as Href);
    } catch (error) {
      toast.show({
        action: "danger",
        description:
          error instanceof Error ? error.message : "Failed to delete player",
      });
    }
  }, [player, deleteManualPlayer, removeRecentPlayer, toast, router]);

  // Transfer reports (SE-3814, SE-3971) — admin always, others need PostHog flag
  // PostHog targets by environment + user role (e.g. MANAGER)
  const transferFlag =
    usePostHogFeatureFlag(POSTHOG_FLAGS.MANUAL_PLAYER_TRANSFER_DATA) === true;
  const canTransferReports = identityData.isManual && (isAdmin || transferFlag);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const transferCounts = player?.transferableDataCounts;
  const sourcePositionAbbr = player?.positions?.[0]?.abbreviation ?? null;
  const isTransferDisabled =
    !transferCounts ||
    transferCounts.scoutReportCount + transferCounts.noteCount === 0;

  const handleTransferReports = useCallback(() => {
    setIsTransferModalOpen(true);
  }, []);

  const handleTransferModalClose = useCallback(() => {
    setIsTransferModalOpen(false);
  }, []);

  // SE-5062: if the user navigates away mid-export, reset the export signal on
  // unmount so DataHubTab does not stay force-mounted forever.
  useEffect(
    () => () => {
      isExportingReportVar(false);
    },
    []
  );

  if (loading && !player) {
    return <PlayerPageHeaderSkeleton />;
  }

  return (
    <>
      <PlayerPageHeaderView
        identityBlockProps={identityBlockProps}
        indicatorRailProps={indicatorRailProps}
        isDark={isDark}
        activeTab={activeTab}
        tabPressHandlers={tabPressHandlers}
        player={player}
        onDownloadReport={handleDownloadReport}
        isGeneratingPdf={isGeneratingPdf}
        showDownloadReport={isDownloadReportEnabled}
        isSmallScreen={isSmallScreen}
        onAddToList={handleOpenActionsheet}
        onAddReport={handleAddReport}
        onEditPlayer={identityData.isManual ? handleEditPlayer : undefined}
        onDeletePlayer={identityData.isManual ? handleDeletePlayer : undefined}
        onTransferReports={
          canTransferReports ? handleTransferReports : undefined
        }
        isTransferDisabled={canTransferReports ? isTransferDisabled : undefined}
        isManual={identityData.isManual}
        isOwner={isOwner}
        isModal={isModal}
      />

      {/* Add to watchlist — scoped legacy picker on desktop; on mobile the
          current per-player "Select list to add" sheet (SE-5439/SE-5438).
          The watchlist chips and the "+" FAB "List" action both drive
          `showActionsheet`, so both now open the modern sheet. */}
      {player &&
        (isSmallScreen ? (
          <SelectListSheet
            isOpen={showActionsheet}
            onClose={handleCloseActionsheet}
            playerId={player.id}
            playerName={identityData.playerName}
            onMembershipChange={handleWatchlistMembershipChange}
          />
        ) : (
          <ScopedWatchlistPicker
            player={player as unknown as PlayerSearchPlayerFragment}
            isOpen={showActionsheet}
            onClose={handleCloseActionsheet}
            onAddWatchlistPress={handleAddWatchlistPress}
            loading={watchlistLoading}
            isDark={isDark}
            promptRef={watchlistPromptRef}
          />
        ))}

      {editData ? (
        <ManuallyAddPlayerModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          editData={editData}
        />
      ) : null}

      {identityData.isManual && player ? (
        <DeletePlayerDialog
          isOpen={isDeleteDialogOpen}
          isDeleting={isDeletingPlayer}
          playerName={player.name}
          scoutReportsCount={player.scoutReports?.length ?? 0}
          onClose={handleDeleteDialogClose}
          onConfirmDelete={handleConfirmDelete}
        />
      ) : null}

      {/* SE-5473: in-place KPI breakdown for the preview-modal context. Only
          the modal context sets `openBreakdownSlug` (the full page navigates
          instead), so this renders null on the page. */}
      <PlayerPreviewBreakdownModal
        slug={openBreakdownSlug}
        playerId={player?.id}
        onClose={handleCloseBreakdown}
      />

      {canTransferReports && player ? (
        <TransferReportsModal
          isOpen={isTransferModalOpen}
          sourcePlayerId={player.id}
          sourcePlayerName={player.name}
          sourcePositionAbbreviation={sourcePositionAbbr}
          sourceTeamName={player.team?.name ?? player.teamName ?? null}
          scoutReportCount={transferCounts?.scoutReportCount ?? 0}
          noteCount={transferCounts?.noteCount ?? 0}
          onClose={handleTransferModalClose}
        />
      ) : null}
    </>
  );
};

PlayerPageHeaderInnerContainer.displayName = "PlayerPageHeaderInnerContainer";

export default PlayerPageHeaderInnerContainer;
